import { DsdFile } from "@/types/dsd";
import { DeletedUserAvatar } from "@/components/common/deleted-user-avatar";
import {
  PeriodChip,
  ReportPeriod,
  ReportType,
  TypeChip,
  YearChip,
} from "@/components/common/report-chip";
import { useUserNames } from "@/hooks/queries/use-user-names";
import { Avatar } from "@/toggle-ui-kit/avatar";
import { getFormattedDateCompact } from "@/utils/time";
import { getUserDisplayName, isDeletedUser } from "@/utils/user-display-name";

/**
 * 2단계 상단에 고정으로 보여주는 "불러올 원본 보고서" 요약 카드 — Figma 5611:341247.
 * 회색 배경(Background/Gray/Alternative) + 칩·파일명 한 줄, 업로더·업로드일 한 줄.
 */
export const ForwardReportSummary = ({ file }: { file: DsdFile }) => {
  const { data: userNamesMap, isLoading } = useUserNames([file.creator]);

  const year = file.reportYear ?? "-";
  const period = file.reportQuarter as ReportPeriod | null;
  const reportType = file.financialStatementType as ReportType | null;
  const fileName = file.displayName ?? file.name;
  const uploader = getUserDisplayName(userNamesMap, file.creator);
  const isDeletedUploader = isDeletedUser(userNamesMap, file.creator);

  return (
    <div className="flex w-full shrink-0 items-center gap-3 rounded-[8px] bg-background-gray-alternative p-4">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex w-full items-center gap-2">
          <div className="flex shrink-0 items-center gap-1">
            {reportType && <TypeChip type={reportType} />}
            <YearChip year={year} />
            {period && <PeriodChip period={period} />}
          </div>
          <p className="label-1-normal-medium min-w-0 flex-1 truncate text-label-normal">
            {fileName}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            {!isLoading &&
              (isDeletedUploader ? (
                <DeletedUserAvatar size="16" />
              ) : (
                <Avatar email={file.creator} displayName={uploader} size="16" />
              ))}
            <span className="caption-1-medium text-label-alternative">
              {isLoading ? "..." : uploader}
            </span>
          </div>
          <span className="caption-1-medium text-label-alternative">
            {getFormattedDateCompact(file.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
};
